<template>
  <BaseHero
    :class="classes"
    :overlay-opacity="content.overlayOpacity"
    overlay-gradient
  >
    <template #media>
      <div
        :id="swiperId"
        class="h-full swiper-container"
      >
        <div class="HeroCarousel__wrapper swiper-wrapper">
          <div
            v-for="(slide, index) in content.slides"
            :key="index"
            class="HeroCarousel__slide swiper-slide"
          >
            <HeroMedia
              :media="slide.media"
              :is-first="index === 0"
            />
          </div>
        </div>
      </div>
    </template>

    <template #content>
      <VisionLink
        v-if="currentSlideSlug"
        class="HeroCarousel__link"
        :to="currentSlideSlug"
        display="inline-block"
      >
        <Transition
          name="fade"
          mode="out-in"
        >
          <BaseCopy
            v-if="currentSlidePreTitle"
            :key="currentSlideTitle"
            size="h5"
            class="HeroCarousel__date"
          >
            {{ currentSlidePreTitle }}
          </BaseCopy>
        </transition>

        <Transition
          name="fade"
          mode="out-in"
        >
          <VisionHeading
            v-if="currentSlideTitle"
            :key="currentSlideTitle"
            tag="h1"
            size="h1"
            class="HeroCarousel__title"
          >
            {{ currentSlideTitle }} <span
              v-if="currentSlideSubtitle"
              :class="$b('subtitle')"
            >- {{ currentSlideSubtitle }}</span>
          </VisionHeading>
        </Transition>
      </VisionLink>

      <ul class="HeroCarousel__pagination">
        <li
          v-for="(item, index) in content.slides"
          :key="index"
          class="HeroCarousel__pagination-item"
        >
          <button
            class="HeroCarousel__pagination-link"
            @click="transitionToSlide(index)"
          >
            <div class="HeroCarousel__pagination-bar">
              <div
                class="HeroCarousel__pagination-progress"
                :class="{
                  'is-active': swiper && swiper.autoplay.running && activeSlideIndex === index
                }"
                :style="paginationProgressStyles(index)"
              />
            </div>

            <VisionHeading
              v-if="item.title"
              tag="h2"
              size="h4"
              class="HeroCarousel__pagination-title"
              :style="paginationTitleStyles(index)"
            >
              {{ item.title }}
              <span v-if="item.subtitle">
                - {{ item.subtitle }}
              </span>
            </VisionHeading>
          </button>
        </li>
      </ul>
    </template>
  </BaseHero>
</template>

<script>
import SwiperClass from 'swiper'
import { Autoplay, EffectFade, A11y, Keyboard } from 'swiper/modules';

SwiperClass.use([Autoplay, EffectFade, A11y, Keyboard])

export default {
  name: 'HeroCarousel',

  props: {
    name: {
      type: String,
      required: true
    },

    content: {
      type: Object,
      required: true
    }
  },

  setup () {
    const { resolveLocalePath } = useUrlBuilder()
    return { resolveLocalePath }
  },

  data () {
    return {
      swiper: null,
      firstSlide: true,
      isReady: false,
      activeSlideIndex: 0
    }
  },

  computed: {
    swiperId () {
      return `${this.name}-swiper`
    },

    config () {
      return {
        a11y: true,
        autoplay: {
          delay: this.content.slideDuration,
          disableOnInteraction: false
        },
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        init: false,
        keyboard: {
          enabled: true
        },
        loop: true,
        speed: this.content.slideTransitionDuration,
        on: {
          realIndexChange: (e) => {
            this.activeSlideIndex = e.realIndex
          }
        }
      }
    },

    classes () {
      return {
        HeroCarousel: true,
        'is-ready': this.isReady
      }
    },

    currentSlide () {
      if (!this.swiper) return 0

      return this.content.slides[this.activeSlideIndex]
    },

    currentSlideDate () {
      if (!this.currentSlide) return null
      
      const date = this.currentSlide.date
        || this.currentSlide._firstPublishedAt
        || this.currentSlide.createdAt
      
      return this.$dayjs(date).format('D MMMM YYYY')
    },

    currentSlideTitle () {
      return this.currentSlide?.title
    },

    currentSlideSubtitle () {
      return this.currentSlide?.subtitle
    },

    currentSlideSlug () {
      return this.resolveLocalePath(this.currentSlide?.slug)
    },

    currentSlidePreTitle () {
      if (
        this.currentSlide?.showCategory &&
        this.currentSlide?.category?.title
      ) {
        return this.currentSlide.category.title
      }

      return this.currentSlideDate
    },

    progressBarDuration () {
      return this.firstSlide
        ? `${this.content.slideDuration}ms`
        : `${
          this.content.slideDuration + this.content.slideTransitionDuration
        }ms`
    }
  },

  mounted () {
    if (!this.swiper) this.createSwiper()

    this.$nextTick(() => {
      this.swiper?.init()
      this.swiper?.on('slideChange', () => { this.firstSlide = false })
      this.isReady = true
    })
  },

  beforeUnmount () {
    if (this.swiper) this.swiper.destroy()
  },

  methods: {
    createSwiper () {
      this.swiper = new SwiperClass(`#${this.swiperId}`, this.config)
    },

    paginationProgressStyles (index) {
      if (this.swiper && this.activeSlideIndex === index) {
        return { transitionDuration: this.progressBarDuration }
      }
    },

    paginationTitleStyles (index) {
      if (this.swiper && this.activeSlideIndex === index) return { opacity: 1 }
    },

    transitionToSlide (index) {
      this.swiper.slideToLoop(index, this.content.slideTransitionDuration)
    },

    getMediaForSlide({ image, mobileImage, mobileVideo, video }) {
      return { image, mobileImage, mobileVideo, video }
    }
  }
}
</script>

<style lang="postcss">
.is-ready.HeroCarousel {
  @apply opacity-100;
}

.HeroCarousel {
  @apply relative
    opacity-0;

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity .3s ease-in-out;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}

.HeroCarousel__wrapper {
  transition-property: opacity !important;
}

.HeroCarousel__slide {
  @apply w-full
    transition-opacity;
}

.HeroCarousel__date {
  @apply mb-xs
    uppercase
    md:mb-sm;
}

.HeroCarousel__title {
  @screen short {
    @apply text-2xl;
  }
}

.HeroCarousel__subtitle {
  @screen short {
    @apply hidden;
  }

  @screen md {
    @apply hidden;
  }
}

.HeroCarousel__pagination {
  @apply flex
    mt-sm;

  @screen md {
    margin-top: 32px;
  }
}

.HeroCarousel__pagination-item {
  @apply w-full
    -my-xxxs;

  &:first-of-type {
    @apply md:-ml-xxxs;
  }

  &:last-of-type {
    @apply md:-mr-xxxs;
  }

  &:not(:first-of-type) {
    @apply ml-xxxs;

    @screen md {
      margin-left: calc(32px - (theme('spacing.xxxs') * 2));
    }
  }
}

.HeroCarousel__link,
.HeroCarousel__pagination-link {
  @apply text-white;

  &:hover,
  &:hover:not(.disabled):not(:disabled) {
    background-position: 0;
    color: theme('colors.white');
  }
}

.HeroCarousel__link {
  @apply inline-block
    md:p-xxxs
    md:-m-xxxs;

  transition: opacity .3s ease-in-out;

  @screen md {
    &:hover {
      @apply opacity-75;
    }
  }
}

.HeroCarousel__pagination-link {
  @apply block
    w-full
    py-xxxs
    md:p-xxxs;

  &:hover .HeroCarousel__pagination-title {
    @apply md:opacity-100;
  }
}

.HeroCarousel__pagination-bar {
  @apply flex;

  background-color: rgba(255, 255, 255, 0.4);
  height: 2px;
}

.HeroCarousel__pagination-progress {
  @apply w-full
    bg-white
    h-full;
  transform: scaleX(0);
  transform-origin: center left;

  &.is-active {
    transform: scaleX(1);
    transition-property: transform;
    transition-timing-function: linear;
  }
}

.HeroCarousel__pagination-title {
  @apply mt-sm
    opacity-40
    text-left
    mdMax:hidden;

  transition: opacity .3s ease-in-out;
}
</style>
